import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, Login, ProtectedRoute, PublicRoute } from './modules/auth';
import { Sidebar, TopBar } from './modules/layout';
import { ProviderSearch } from './modules/providers';
import { UserManagement } from './modules/users';
import { VisitTracker } from './modules/visits';
import { Card, CardContent } from './components/ui/card';

const MainApp: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Get active tab from URL path
  const activeTab = location.pathname.substring(1) || 'search';

  const getPageTitle = () => {
    switch (activeTab) {
      case 'search':
        return 'Provider Search';
      case 'users':
        return 'User Management';
      case 'visits':
        return 'Visit Tracker';
      default:
        return 'Provider Search';
    }
  };

  const getPageSubtitle = () => {
    switch (activeTab) {
      case 'search':
        return 'Search Medicare providers by location, NPI, or service';
      case 'users':
        return 'Manage user accounts and permissions';
      case 'visits':
        return 'Track and manage provider visits';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        activeTab={activeTab} 
        onTabChange={(tab) => navigate(`/${tab}`)}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
      
      <div className="xl:ml-64">
        <TopBar 
          title={getPageTitle()} 
          subtitle={getPageSubtitle()}
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />
        
        <main className="p-3 lg:p-4">
          <Routes>
            <Route path="/search" element={<ProviderSearch />} />
            <Route path="/users" element={
              user?.role === 'admin' ? (
                <UserManagement />
              ) : (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-12">
                      <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
                      <p className="text-muted-foreground">
                        You don't have permission to access this page.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )
            } />
            <Route path="/visits" element={<VisitTracker />} />
            <Route path="/" element={<Navigate to="/search" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainApp />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
